import React, { lazy, Suspense, useEffect, useState } from "react";
import Button from "@/components/forms/Button/Button";
import { Product, useInventoryContext } from "@/contexts/InventoryContext";
import styles from "./InventoryTable.module.css";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { useLocation, useSearch } from "wouter";
import VariableBuyCostModal from "@/app/inventory/VariableBuyCostModal/VariableBuyCostModal";
import { useBuyCosts } from "@/components/VariableBuyCost/hooks/useBuyCosts";
import BuyCostDisplay from "@/components/VariableBuyCost/BuyCostDisplay";
import InventoryExportModal from "../InventoryExportModal/InventoryExportModal";
import IconClose from "@/components/icons/IconClose";
import IconBoxOulined from "@/components/icons/IconBoxOutlined";
import IconImport from "@/components/icons/IconImport";
import IconExport from "@/components/icons/IconExport";
import { Table, TableColumn, TableState } from "@/components/Table/Table";
import Skeleton from "@/components/Skeleton/Skeleton";
import IconFbaBoxOutlined from "@/components/icons/IconFbaBoxOutlined";
import { getLocalStorageItem, LocalStorageKeys } from "@/utils/localStorageUtils";
import { formatISODate } from "@/utils/dateUtils";
import { formatValue } from "@/utils/formatUtils";
import { productConditionToString } from "@/app/fba/utils";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import InventoryQuantity from "../InventoryQuantity/InventoryQuantity";
import Pagination from "@/components/Pagination/Pagination";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";
import ListingStatusBadge from "@/components/ListingStatusBadge/ListingStatusBadge";
import SearchInput from "@/components/SearchInput";
import InventoryQuantityModal from "../InventoryQuantityModal/InventoryQuantityModal";
import CopyToClipBoard from "@/components/CopyToClipboard/CopyToClipboard";
import Flex from "@/components/Flex/Flex";
import { logRocketError } from "@/utils/logUtils";

const LazyImportModal = lazy(() => import("@/app/inventory/BuyCostImportModal/BuyCostImportModal"));

interface InventoryTableState {
  sortedColumnId: string | undefined;
  sortDirection: 'asc' | 'desc' | undefined;
  page: number;
  limit: number;
}

export default function InventoryTable() {
  // the whole inventory, flattened so if you have multiple SKUs within one ASIN that shows up as multiple rows.
  const { inventoryFlat, isFetchingInventory, refetchAnalytics, refetchWholeInventory } = useInventoryContext();
  const searchParams = useSearch();
  const [location, navigate] = useLocation();
  const [searchInputText, setSearchInputText] = useState<string>(() => {
    if (searchParams.includes("skus")) {
      const split = searchParams.split("=");
      return split[1].replace(",", " ");
    }
    
    return "";
  });
  // after applying sorting & filters:
  const [filteredInventory, setFilteredInventory] = useState<Product[]>([]);
  const [buyCostImportModalOpen, setBuyCostImportModalOpen] = useState<boolean>(false);
  const [variableBuyCostModalOpen, setVariableBuyCostModalOpen] = useState<boolean>(false);
  const [inventoryExportModalOpen, setInventoryExportModalOpen] = useState<boolean>(false);
  const [inventoryQuantityModalOpen, setInventoryQuantityModalOpen] = useState<boolean>(false);
  const [selectedInventoryItemForVariableBuyCost, setSelectedInventoryItemForVariableBuyCost] = useState<Product | null>(null);
  const [selectedInventoryItemForEditQuantity, setSelectedInventoryItemForEditQuantity] = useState<Product | null>(null);
  const [fixedBuyCostWarningModalOpen, setFixedBuyCostWarningModalOpen] = useState<boolean>(false);
  const { loading, convertToFixedBuyCost } = useBuyCosts(null);
  const filtersActive = searchInputText !== "";
  const tableName = "inventoryTable";
  const [tableState, setTableState] = useState<InventoryTableState>(() => {
    const tableProfile: TableState = getLocalStorageItem(`${LocalStorageKeys.tableStatePrefix}${tableName}`);

    return {
      sortedColumnId: tableProfile?.sortedColumnId ?? undefined,
      sortDirection: tableProfile?.sortDirection ?? undefined,
      page: 1,
      limit: 10,
    };
  });

  const getFilteredInventory = (inventory: Product[]) => {
    const columnDataKey = columns?.find((col) => col.id === tableState.sortedColumnId)?.dataKey as string;

    const newSortedData = [...inventory].sort((a, b) => {
      if (tableState.sortedColumnId) {
        let aValue: any = 0;
        let bValue: any = 0;

        //Have to do this because some values are on the products root scope and others are in its seller data scope.
        if (!!a[columnDataKey]) {
          aValue = a[columnDataKey];
        } else {
          aValue = a.sellerData[0][columnDataKey];
        }

        if (!!b[columnDataKey]) {
          bValue = b[columnDataKey];
        } else {
          bValue = b.sellerData[0][columnDataKey];
        }

        // Check if values are numeric strings and convert them to numbers for comparison
        if (typeof aValue === 'string' && !isNaN(Number(aValue))) {
          aValue = Number(aValue);
        }
        if (typeof bValue === 'string' && !isNaN(Number(bValue))) {
          bValue = Number(bValue);
        }

        if (tableState.sortDirection === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      }

      return 0;
    });

    // filter data based on search term in product name, sku, or asin
    if (searchInputText && newSortedData) {
      return newSortedData.filter((row) => {
        const searchTermValues = searchInputText.toLowerCase().split(" ");
        const foundItem = searchTermValues.filter(term => row.searchString.includes(term));
        if (foundItem.length > 0 || searchInputText.trim() === "") return row;
      });
    } else {
      return newSortedData;
    }
  };

  const handleBuyCostModalOpenChange = (isOpen: boolean, refetchInventory: boolean) => {
    if (refetchInventory) {
      refetchAnalytics()
      refetchWholeInventory()
    }
    setBuyCostImportModalOpen(isOpen);
  };

  const handleClearFilters = () => {
    navigate("~/inventory");
    setSearchInputText("");
  };

  const handleSortChange = async (columnId: string, direction: 'asc' | 'desc') => {
    setTableState({
      ...tableState,
      sortedColumnId: columnId,
      sortDirection: direction,
      page: 1,
    });
  };

  const handlePageSizeChange = async (newPageSize: number) => {
    setTableState({
      ...tableState,
      page: 1,
      limit: newPageSize,
    });
  };

  const handlePageChange = async (newPage: number) => {
    setTableState({
      ...tableState,
      page: newPage,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchInputText(value);
    if (value && tableState.page !== 1) {
      setTableState({
        ...tableState,
        page: 1,
      });
    }
  };

  const columns: TableColumn<Product>[] = [
    {
      title: 'Products',
      dataKey: 'name',
      id: 'products',
      sortable: true,
      width: '21rem',
      pinned: 'left',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.listingItem}>
          <img
            src={row.getCellData('image')}
            alt={row.getCellData('name')}
            className={styles.listingImage}
          />
          <div className={styles.listingItemDetail}>
            <Tooltip title={row.getCellData('name')}>
              <div className={styles.listingName}>
                <a href={`https://www.amazon.com/dp/${row.getCellData('asin')}`} target="_blank" className={styles.listingName}>
                  {row.getCellData('name')}
                </a>
              </div>
            </Tooltip>
            <CopyToClipBoard value={row.getCellData('asin')} tooltipText={"Copy ASIN"}>
              <span className={`weak ${styles.listingAsin}`}>ASIN: <span className="strong">{row.getCellData('asin')}</span></span>
            </CopyToClipBoard>
          </div>
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.listingItem}>
          <Skeleton type='image' height={60} width={60} />
          <div className={styles.listingItemDetail}>
            <Skeleton type='line-medium' />
            <Skeleton type='line-short' />
          </div>
        </div>
      ),
    },
    {
      title: 'Sku',
      dataKey: 'sku',
      id: 'sku',
      sortable: true,
      width: '13rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.listingItem}>
          <div className={styles.listingItemSkuDetail}>
            <CopyToClipBoard
              value={row.getCellData('sellerData')[0].sku}
              tooltipText="Copy SKU"
            >
              <span className={styles.listingName}>{row.getCellData('sellerData')[0].sku}</span>
            </CopyToClipBoard>
            <div className={styles.listingItemSubDetail}>
              <span className="weak">{productConditionToString(row.getCellData('sellerData')[0].condition)}</span>
              <StatusBadge
                status={row.getCellData('sellerData')[0].isFBM ? "danger" : "warning"}
                label={row.getCellData('sellerData')[0].isFBM ? "FBM" : "FBA"}
              />
            </div>
          </div>
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.listingItem}>
          <div className={styles.listingItemDetail}>
            <Skeleton type='line-medium' />
            <Skeleton type='line-short' />
          </div>
        </div>
      ),
    },
    {
      title: 'Submitted',
      dataKey: 'submittedAt',
      id: 'submitted',
      sortable: true,
      visible: false,
      width: '11.5rem',
      alignment: 'left',
      renderRowCell: (row) => formatISODate(row.getCellData('sellerData')[0].submittedAt),
    },
    {
      title: 'Status',
      dataKey: 'status',
      id: 'status',
      sortable: false,
      width: '8rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <ListingStatusBadge
          sellerData={row.getCellData('sellerData')[0]}
          proccessingTooltipPosition="top"
        />
      ),
    },
    {
      title: 'List price',
      dataKey: 'listPrice',
      id: 'list-price',
      sortable: true,
      width: '7.5rem',
      alignment: 'left',
      renderRowCell: (row) => formatValue(row.getCellData('sellerData')[0].listPrice, 'currency'),
    },
    {
      title: 'Qty in stock',
      dataKey: 'quantity',
      id: 'qty-in-stock',
      sortable: true,
      width: '7.5rem',
      alignment: 'left',
      renderRowCell: (row) => {
        if (row.getCellData('sellerData')[0].isFBM) {
          return (
            <InventoryQuantity
              value={row.getCellData('sellerData')[0].quantity}
              rowStatus={row.getCellData('sellerData')[0].status}
              onClick={() => {
                setSelectedInventoryItemForEditQuantity(row.rowData);
                setInventoryQuantityModalOpen(true);
              }}
            />
          );
        } else {
          return formatValue(row.getCellData('sellerData')[0].quantity, 'number');
        }
      },
    },
    {
      title: 'Buy cost',
      dataKey: 'buyCost',
      id: 'buy-cost',
      sortable: true,
      width: '8rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <BuyCostDisplay
          value={Number(row.getCellData('buyCost'))}
          onClick={() => {
            setSelectedInventoryItemForVariableBuyCost(row.rowData);
            setVariableBuyCostModalOpen(true);
          }}
        />
      )
    }
  ];

  useEffect(() => {
    if (inventoryFlat) {
      setFilteredInventory(getFilteredInventory(inventoryFlat));
    }
  }, [
    searchInputText,
    inventoryFlat,
    tableState.page,
    tableState.limit,
    tableState.sortDirection,
    tableState.sortedColumnId,
  ]);

  const filteredInventoryPage = filteredInventory.slice((tableState.page - 1) * tableState.limit, tableState.page * tableState.limit);

  return (
    <>
      <Table
        data={filteredInventoryPage}
        columns={columns}
        tableName={tableName}
        className={styles.table}
        isLoading={isFetchingInventory}
        skeletonRows={tableState.limit}
        sortDirection={tableState.sortDirection}
        sortedColumnId={tableState.sortedColumnId}
        onSort={handleSortChange}
        showSort
        header={
          <>
            <div className={styles.searchInputContainer}>
              <SearchInput
                searchTerm={searchInputText}
                onDebounceChange={handleSearchChange}
                placeholder="Search by product name, SKU, FNSKU, ASIN, UPC, ISBN, EAN, or GTIN"
                className={styles.searchInput}
              />
              {filtersActive && (
                <Button
                  type={"secondary"}
                  onClick={handleClearFilters}
                >
                  <IconClose />
                  <span>Clear</span>
                </Button>
              )}
            </div>
          </>
        }
        headerControls={
          <Flex gap={2}>
            <Button
              type={"secondary"}
              onClick={() => navigate("~/fbm/listings/create")}
              className={styles.headerControlButton}
            >
              <IconBoxOulined size={18} />
              <span>List FBM</span>
            </Button>
            <Button
              type={"secondary"}
              onClick={() => navigate("~/fba/listings/create")}
              className={styles.headerControlButton}
            >
              <IconFbaBoxOutlined size={18} />
              <span>List FBA</span>
            </Button>
            <Button
              type={"secondary"}
              onClick={() => setBuyCostImportModalOpen(true)}
            >
              <IconImport size={20} />
              <span>Import</span>
            </Button>
            <Button
              type={"secondary"}
              onClick={() => setInventoryExportModalOpen(true)}
            >
              <IconExport size={20} />
              <span>Export</span>
            </Button>
          </Flex>
        }
        footer={
          <Pagination
            total={filteredInventory.length || 0}
            pageSize={tableState.limit}
            onChangePageSize={handlePageSizeChange}
            page={tableState.page}
            onChangePage={handlePageChange}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        }
      />
      {inventoryQuantityModalOpen && (
        <InventoryQuantityModal
          inventoryItem={selectedInventoryItemForEditQuantity}
          open={inventoryQuantityModalOpen}
          onOpenChange={(isOpen) => {
            setSelectedInventoryItemForEditQuantity(null);
            setInventoryQuantityModalOpen(isOpen);
          }}
          onClose={() => {
            setSelectedInventoryItemForEditQuantity(null);
            setInventoryQuantityModalOpen(false);
          }}
        />
      )}
      {buyCostImportModalOpen &&
        <Suspense fallback={<div>Loading...</div>}>
          <LazyImportModal
            open={buyCostImportModalOpen}
            onBuyCostModalOpenChange={handleBuyCostModalOpenChange}
            closeOnOutsideClick={true}
          />
        </Suspense>
      }
      {variableBuyCostModalOpen &&
        <VariableBuyCostModal
          inventoryItem={selectedInventoryItemForVariableBuyCost}
          open={variableBuyCostModalOpen}
          onOpenChange={(isOpen) => { setSelectedInventoryItemForVariableBuyCost(null); setVariableBuyCostModalOpen(isOpen) }}
          onClose={() => { setSelectedInventoryItemForVariableBuyCost(null); setVariableBuyCostModalOpen(false) }}
        />
      }
      {inventoryExportModalOpen &&
        <InventoryExportModal open={inventoryExportModalOpen} onModalOpenChange={setInventoryExportModalOpen} />}
      {fixedBuyCostWarningModalOpen &&
        <ConfirmModal
          loading={loading}
          open={fixedBuyCostWarningModalOpen}
          onClose={async (confirmed) => {
            if (confirmed) {
              await convertToFixedBuyCost(selectedInventoryItemForVariableBuyCost.sellerData[0].productId);
              setFixedBuyCostWarningModalOpen(false)
            } else {
              setFixedBuyCostWarningModalOpen(false)
            }
          }}
          title="Irreversible Action"
          body={
            <p style={{ margin: "20px" }}>
              Clicking 'Confirm' will delete all other cost history you entered except for the most recent one
            </p>
          }
          confirmButtonLabel="Confirm and only keep the most recent cost"
        />
      }
    </>
  );
}